import React, { useEffect } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate 
} from 'react-router-dom';
import useAuthStore from '@/lib/stores/authStore';
import { ROUTES } from '@/types';

// Page Components
import LoginPage from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import { RestaurantsPage } from '@/pages/restaurants';
import { MenuBuilderPage, ImageMenuPage } from '@/pages/menu';
import { QRCodeManagementPage } from '@/pages/qr';
import { PublicMenuPage } from '@/pages/public';

// Loading Component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
    <div className="text-center">
      <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 animate-pulse">
        <img 
          src="/logo.svg" 
          alt="Qodrix Menu" 
          className="h-20 w-20 object-contain"
        />
      </div>
      <p className="text-gray-600 text-sm">Initializing...</p>
    </div>
  </div>
);

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, accessState, fetchUserProfile } = useAuthStore();

  // Fetch user profile on mount if we have a user but no access state
  useEffect(() => {
    if (user && !accessState) {
      fetchUserProfile();
    }
  }, [user, accessState, fetchUserProfile]);

  // Redirect to signin if no user
  if (!user) {
    return <Navigate to={ROUTES.SIGNIN} replace />;
  }

  // If we're still loading access state, show loading
  if (!accessState) {
    return <LoadingSpinner />;
  }

  // If access is expired, the children components will handle showing the account modal
  // We don't redirect because the user should be able to extend their access
  return <>{children}</>;
};

// Public Route Component (redirect to restaurants if authenticated)
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user } = useAuthStore();

  return user ? <Navigate to={ROUTES.RESTAURANTS} replace /> : <>{children}</>;
};

function App() {
  const { initialize, initialized } = useAuthStore();

  // Initialize auth state on app start
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Show minimal loading only during initial auth check
  if (!initialized) {
    return <LoadingSpinner />;
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {/* Authentication Routes */}
        <Route 
          path={ROUTES.SIGNIN} 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path={ROUTES.SIGNUP} 
          element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          } 
        />
        <Route 
          path={ROUTES.FORGOT_PASSWORD} 
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          } 
        />

        {/* Public Routes (no authentication required) */}
        <Route 
          path={ROUTES.PUBLIC_MENU} 
          element={<PublicMenuPage />} 
        />

        {/* Protected Routes */}
        <Route 
          path={ROUTES.RESTAURANTS} 
          element={
            <ProtectedRoute>
              <RestaurantsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path={ROUTES.MENU_BUILDER} 
          element={
            <ProtectedRoute>
              <MenuBuilderPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path={ROUTES.IMAGE_MENU} 
          element={
            <ProtectedRoute>
              <ImageMenuPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path={ROUTES.QR_CODE_MANAGEMENT} 
          element={
            <ProtectedRoute>
              <QRCodeManagementPage />
            </ProtectedRoute>
          } 
        />

        {/* App Routes - redirect to appropriate destinations */}
        <Route 
          path="/app" 
          element={<Navigate to={ROUTES.RESTAURANTS} replace />} 
        />
        <Route 
          path="/app/*" 
          element={<Navigate to={ROUTES.RESTAURANTS} replace />} 
        />

        {/* Dashboard Routes - redirect to restaurants */}
        <Route 
          path={ROUTES.DASHBOARD} 
          element={<Navigate to={ROUTES.RESTAURANTS} replace />} 
        />

        {/* Catch-all route - redirect authenticated users to restaurants, others to signin */}
        <Route 
          path="*" 
          element={<Navigate to={ROUTES.RESTAURANTS} replace />} 
        />
      </Routes>
    </Router>
  );
}

export default App;